import dayjs from 'dayjs'
import { getWebviewType, WebviewType } from '@/utils/checkDevice.ts'
import { getS3Url } from '@/utils/env'
import toast from '@/utils/toast'

export interface CopyToClipOptions {
  content: string
  msg?: string
  failMsg?: string
  noToast?: boolean
}

export interface ToRemOptions {
  rootValue?: number
  minPixelValue?: number
}

export function pxToRemValue(
  value: string | number | undefined,
  options: ToRemOptions = {}
): string {
  const { rootValue = 100, minPixelValue = 2 } = options

  if (value === undefined || value === null || value === '' || value === 'auto') return 'auto'

  const toRem = (pxValue: number) => {
    if (Math.abs(pxValue) <= minPixelValue) return `${pxValue}px`
    return `${pxValue / rootValue}rem`
  }

  if (typeof value === 'number') {
    return toRem(value)
  }

  const normalized = value.trim()
  if (!normalized) return 'auto'

  const isPxValue = normalized.toLowerCase().endsWith('px')
  const numericPart = isPxValue ? normalized.slice(0, -2) : normalized
  const parsedValue = Number(numericPart)

  if (!Number.isFinite(parsedValue)) return normalized
  return toRem(parsedValue)
}

export function pxToRemRuntimePx(value: number, options: Pick<ToRemOptions, 'rootValue'> = {}) {
  const { rootValue = 100 } = options
  return remToPxValue(value / rootValue)
}
// 运行时动态rem转px
export function remToPxValue(value: string | number | undefined) {
  if (value === undefined || value === null || value === '') return 0

  if (typeof value === 'number') {
    return value * getRootFontSizePx()
  }

  const normalized = value.trim().toLowerCase()
  if (normalized.endsWith('px')) {
    return Number.parseFloat(normalized)
  }

  const remValue = normalized.endsWith('rem')
    ? Number.parseFloat(normalized.slice(0, -3))
    : Number.parseFloat(normalized)

  return Number.isFinite(remValue) ? remValue * getRootFontSizePx() : 0
}

export function getRootFontSizePx() {
  return Number.parseFloat(getComputedStyle(document.documentElement).fontSize)
}

// 在手机  添加到桌面模式  window.open无效
export function openNewWindow(url?: string) {
  if (!url) return
  createLink(url)
}

export function createLink(url: string, sync = false) {
  if (sync) {
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.target = isStandalone() ? '_self' : '_blank'
    anchor.click()
  } else {
    setTimeout(() => {
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.target = isStandalone() ? '_self' : '_blank'
      anchor.click()
    }, 50)
  }
}

// 是否运行在手机主屏幕模式
export function isStandalone() {
  return (
    window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone
  )
}

// 基础的复制方法
export const basicCopy = (content: string, msg = '', noToast = false) => {
  const aux = document.createElement('input')
  aux.setAttribute('value', content)
  aux.setAttribute('readOnly', 'true')
  document.body.appendChild(aux)
  aux.select()
  document.execCommand('copy')
  document.body.removeChild(aux)
  // PC端简化toast提示
  if (!noToast) {
    toast.info(msg)
  }
}

// 复制方法
export const copyToClip = ({ content, msg = '', noToast = false }: CopyToClipOptions) => {
  if (!content) {
    console.error('Content to copy is required')
    return
  }
  // PC端简化国际化处理
  msg = msg || '复制成功'

  if (
    window.navigator.clipboard &&
    window.navigator.clipboard.writeText &&
    getWebviewType() !== WebviewType._android
  ) {
    window.navigator.clipboard
      .writeText(content)
      .then(() => {
        if (!noToast) {
          toast.info(msg)
        }
      })
      .catch(() => {
        basicCopy(content, msg, noToast)
      })
  } else {
    basicCopy(content, msg, noToast)
  }
}

export function addHttpPrefix(image?: string) {
  if (!image) return undefined
  if (image?.startsWith('http') || image?.startsWith('//') || image?.startsWith('blob'))
    return image

  // 清理 baseUrl 末尾的斜杠和 image 开头的斜杠，然后用单个斜杠连接
  const baseUrl = (import.meta.env.VITE_S3_URL || '').replace(/\/+$/, '')
  const path = image.replace(/^\/+/, '')
  return `${baseUrl}/${path}`
}

/**
 * @description 日期本地格式化
 */
const getPreciseFormatter = ({
  formatter = 'YYYY-MM-DD',
  onlyDate = false,
  onlyHourMinute = false
}) => {
  if (!onlyDate) {
    return formatter + (onlyHourMinute ? ' HH:mm' : ' HH:mm:ss')
  }
  return formatter
}

export const formatDateLocal = ({
  locale = 'zh-CN',
  date = dayjs(),
  onlyDate = false,
  onlyYearMonth = false,
  onlyHourMinute = false
}: {
  locale?: string
  date: dayjs.ConfigType
  onlyDate?: boolean
  onlyYearMonth?: boolean
  onlyHourMinute?: boolean
}) => {
  if (onlyYearMonth) {
    return dayjs(date).format('MM-DD')
  }

  const formatter = getPreciseFormatter({
    formatter: locale === 'zh-CN' ? 'YYYY-MM-DD' : 'DD-MM-YYYY',
    onlyDate,
    onlyHourMinute
  })

  return dayjs(date).format(formatter)
}

/**
 * 下载图片到本地
 * @description 通过 fetch 获取图片并触发浏览器下载，失败时尝试新窗口打开
 * @param {string | undefined} url - 图片地址
 * @param {string} name - 下载文件名
 * @param {() => void} callback - 下载完成回调函数（可选）
 * @example
 * ```typescript
 * await downloadImage('https://example.com/image.webp', 'my-image.webp')
 *
 * // 带回调
 * await downloadImage('https://example.com/image.webp', 'my-image.webp', () => {
 *   console.log('下载完成')
 * })
 * ```
 */
export async function downloadImage(
  url: string | undefined,
  name: string,
  callback?: () => void
): Promise<void> {
  if (!url) return
  try {
    const response = await fetch(url)
    const blob = await response.blob()
    const reader = new FileReader()
    reader.onloadend = function () {
      const a = document.createElement('a')
      const event = new MouseEvent('click')
      a.download = name
      a.href = reader.result as string
      a.dispatchEvent(event)
      setTimeout(() => {
        callback && callback()
      }, 1500)
    }
    reader.readAsDataURL(blob)
  } catch {
    // 出现错误比如跨域问题，直接打开图片地址
    openNewWindow(url)
  }
}

// 今日
export function getTodayRange() {
  const now = new Date()
  const startTime = new Date(now)
  const endTime = new Date(now)

  startTime.setDate(now.getDate())
  startTime.setHours(0, 0, 0, 0)

  endTime.setDate(now.getDate())
  endTime.setHours(23, 59, 59, 999)
  return {
    startTime: startTime.getTime(),
    endTime: endTime.getTime()
  }
}

// 昨日
export function getYesterdayRange() {
  const now = new Date()
  const startTime = new Date(now)
  const endTime = new Date(now)

  startTime.setDate(now.getDate() - 1)
  startTime.setHours(0, 0, 0, 0)

  endTime.setDate(now.getDate() - 1)
  endTime.setHours(23, 59, 59, 999)
  return {
    startTime: startTime.getTime(),
    endTime: endTime.getTime()
  }
}

// 本周
export function getThisWeekRange() {
  const now = new Date()
  const dayOfWeek = now.getDay()
  const dayDiffToMonday = (dayOfWeek === 0 ? -6 : 1) - dayOfWeek
  const weekStart = new Date(now)
  weekStart.setDate(now.getDate() + dayDiffToMonday)
  weekStart.setHours(0, 0, 0, 0)

  const weekEnd = new Date(weekStart)
  weekEnd.setDate(weekStart.getDate() + 6)
  weekEnd.setHours(23, 59, 59, 999)

  return {
    startTime: weekStart.getTime(),
    endTime: weekEnd.getTime()
  }
}

// 上周
export function getLastWeekRange() {
  const now = new Date()
  const currentDay = now.getDay() // 获取当前是星期几（0-6, 0为周日）

  // 计算上周一和上周日的日期
  const lastMonday = new Date(now)
  const lastSunday = new Date(now)

  // 上周一的日期
  lastMonday.setDate(now.getDate() - ((currentDay + 6) % 7) - 7) // 上周一
  lastMonday.setHours(0, 0, 0, 0) // 凌晨

  // 上周日的日期
  lastSunday.setDate(now.getDate() - ((currentDay + 6) % 7) - 1) // 上周日
  lastSunday.setHours(23, 59, 59, 999) // 晚上

  // 格式化日期为字符串
  // const formatDate = (date) => date.toISOString().split('T')[0]

  return {
    startTime: lastMonday.getTime(),
    endTime: lastSunday.getTime()
  }
}

// 本月
export function getThisMonthRange() {
  const now = new Date() // 获取当前日期时间
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1) // 获取本月第一天的日期时间
  const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59) // 获取本月最后一天的日期时间（时间为23:59:59）
  return {
    startTime: monthStart.getTime(),
    endTime: monthEnd.getTime()
  }
}

// 过去n天
export function getLastDaysRange(days: number = 1) {
  const now = new Date()
  const startTime = new Date(now)
  const endTime = new Date(now)

  startTime.setDate(now.getDate() - days)
  startTime.setHours(0, 0, 0, 0)

  endTime.setDate(now.getDate())
  endTime.setHours(23, 59, 59, 999)
  return {
    startTime: startTime.getTime(),
    endTime: endTime.getTime()
  }
}

// 通过s3-url获取图片完整地址
export function getImageFullUrlByS3(path?: string) {
  if (!path) return ''
  if (path.startsWith('http') || path.startsWith('//') || path.startsWith('blob:')) return path

  const s3Url = getS3Url().replace(/\/+$/, '')
  const normalizedPath = path.replace(/^\/+/, '')

  if (!s3Url) return normalizedPath
  return `${s3Url}/${normalizedPath}`
}
